import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Users, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswords, setShowPasswords] = useState({});

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/clients`, { withCredentials: true });
      setClients(data);
    } catch (error) {
      toast.error('Eroare la încărcarea clienților');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const togglePassword = (clientId) => {
    setShowPasswords((prev) => ({ ...prev, [clientId]: !prev[clientId] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p style={{ fontFamily: 'IBM Plex Sans' }}>Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center space-x-4 mb-8">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Users className="w-8 h-8 text-blue-900" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Gestionare Clienți
            </h1>
            <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
              {clients.length} clienți înregistrați
            </p>
          </div>
        </div>

        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="bg-slate-50 border-b border-slate-200">
            <CardTitle style={{ fontFamily: 'Outfit' }}>Listă Clienți</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {clients.length === 0 ? (
              <div className="p-8 text-center text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
                Nu există clienți înregistrați încă.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ fontFamily: 'IBM Plex Sans' }}>Nume</TableHead>
                      <TableHead style={{ fontFamily: 'IBM Plex Sans' }}>Email</TableHead>
                      <TableHead style={{ fontFamily: 'IBM Plex Sans' }}>Parolă</TableHead>
                      <TableHead style={{ fontFamily: 'IBM Plex Sans' }}>Status</TableHead>
                      <TableHead style={{ fontFamily: 'IBM Plex Sans' }}>Data înregistrării</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                        <TableCell className="font-semibold" style={{ fontFamily: 'IBM Plex Sans' }}>
                          {client.name}
                        </TableCell>
                        <TableCell style={{ fontFamily: 'IBM Plex Sans' }}>{client.email}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <code
                              className="bg-slate-100 px-2 py-1 rounded text-sm"
                              style={{ fontFamily: 'monospace' }}
                              data-testid={`client-password-${client.id}`}
                            >
                              {showPasswords[client.id] ? client.password : '••••••••'}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => togglePassword(client.id)}
                              data-testid={`toggle-password-${client.id}`}
                            >
                              {showPasswords[client.id] ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800">Activ</Badge>
                        </TableCell>
                        <TableCell style={{ fontFamily: 'IBM Plex Sans' }}>
                          {new Date(client.created_at).toLocaleDateString('ro-RO')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;